/**
 * i18next runtime.
 *
 * Namespaces map 1:1 to page-folder groups (see docs/45-I18N-MODULE-LEDGER.md), which makes a
 * namespace simultaneously the lazy-loading unit, the review unit, and the unit a session claims
 * from the ledger. Catalogues load per route, so first paint never carries all ~7,000 strings.
 *
 * Design: docs/43-I18N-ARABIC-DESIGN.md §3.
 */

import i18next, { type i18n as I18nInstance } from 'i18next';
import { initReactI18next } from 'react-i18next';
import { DEFAULT_LOCALE, LOCALE_CODES, LOCALES, type LocaleCode } from './locales';
import { EN_CATALOGUE } from './t';

/** `t.ts` keeps English flat for fast lookup; i18next wants it nested. */
const unflatten = (flat: Record<string, string>): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(flat)) {
    const parts = k.split('.');
    let cur = out;
    for (let i = 0; i < parts.length - 1; i += 1) {
      cur[parts[i]] = cur[parts[i]] ?? {};
      cur = cur[parts[i]] as Record<string, unknown>;
    }
    cur[parts[parts.length - 1]] = v;
  }
  return out;
};

/**
 * Every catalogue in the tree, as lazy importers keyed by path.
 *
 * `import.meta.glob` is resolved by Vite at build time, so each catalogue becomes its own chunk
 * and only the namespaces a route actually uses are fetched. Listing them by hand would rot the
 * moment a session adds a namespace.
 */
// English is deliberately NOT fetched through this map — `loadNamespace` registers it from the
// eagerly-bundled copy in `t.ts` instead. Vite still emits en chunks here, but nothing ever
// requests them.
const catalogues = import.meta.glob<{ default: Record<string, unknown> }>('./locales/*/*.json');

const cataloguePath = (locale: LocaleCode, namespace: string): string =>
  `./locales/${locale}/${namespace}.json`;

/**
 * Every namespace, loaded before first paint.
 *
 * Originally these were lazy per route. That became wrong once extraction moved the app onto the
 * module-level `t` (see `src/i18n/t.ts`): a module-scope column definition calls `t()` at import
 * time, long before any route-level loader could run, and would render its fallback permanently.
 *
 * Eager loading costs about 90 KB gzipped for the whole catalogue in one language — small against
 * this app's 1.4 MB antd chunk, and it removes an entire class of "why is this screen English"
 * bug. Correctness is worth more than the transfer here.
 */
export const CORE_NAMESPACES = [
  'common', 'errors', 'shell', 'auth', 'setup', 'dashboard', 'selfService', 'attendance',
  'leave', 'payroll', 'employees', 'approvals', 'offboarding', 'finance', 'documents',
  'policies', 'warnings', 'org', 'performance', 'kpi', 'requests', 'engagement', 'reports',
  'recruitment', 'candidates', 'onboarding', 'projects', 'admin',
  // Phase 8a — compensatory off / time off in lieu. Small namespace, but eager like its
  // leave/attendance siblings: the claim form and balance widget are meant to be mountable
  // from either module's screens, so there is no single route-level loader to hang a lazy
  // fetch off yet.
  'compOff',
  // Phase 7 / 8c — in-service leave encashment. Same reasoning as 'compOff' immediately
  // above: the request and approval panels are standalone, meant to be mountable from
  // either ESS or People Operations screens, so there is no single route-level loader yet.
  'leaveEncashment',
  // Nova phase 1 (task G1) — the AI assistant's conversation surface. Same reasoning as
  // 'compOff' and 'leaveEncashment': the launcher and conversation drawer are mounted
  // globally from DashboardLayout, not behind a single route-level loader, and module-
  // scope `t()` calls need the catalogue ready before first paint either way.
  'nova',
  // Task E3/E4 — course catalogue and assignment lifecycle. Same reasoning again: the
  // admin screens hang off the Engage workspace and MyLearning off the Self Service
  // workspace, neither behind a single route-level loader. Missing this line is exactly
  // the silent-fallback trap the dispatch called out — a brand new namespace renders
  // English fallbacks everywhere, in both languages, with no error anywhere.
  'learning',
  // Government-case screens (T2.6/T2.7 SPA build) - the PRO queue and case detail both call
  // the module-level `t` from module scope (status/purpose label maps), same reasoning as
  // 'compOff' and 'leaveEncashment' above.
  'governmentCases',
  // Grades admin screen (Phase 3) - a settings tab mounted from settingsSections.tsx's
  // buildSettingsGroups, which has no single route-level loader of its own (same shape as
  // 'compOff'/'leaveEncashment' above), and the module-level `t` convention this codebase
  // uses throughout means the namespace has to be ready before the screen's own render, not
  // fetched by it.
  'grades',
  // Phase 3.5 - the step authoring screen (fields/outcomes/due dates), opened from
  // StepEditor.tsx inside the Lifecycle designer's own modal, which has no single route-level
  // loader either (same shape as 'grades' above) - the module-level `t` convention needs the
  // catalogue ready before that modal's first render, not fetched by it.
  'stepAuthoring',
  // Settings > Templates > Documents > Letters (docs/97 3.2): mounted inside the Templates hub, which has
  // no route-level loader, and module-level t() needs the catalogue before first render.
  'letterTemplates',
] as const;

export const DEFAULT_NAMESPACE = 'common';

const loaded = new Set<string>();

/**
 * Missing keys.
 *
 * Development logs loudly and records the key, so `pnpm i18n:check` and a browser session both
 * surface gaps. Production falls back to English silently — a user must never see a raw key, and
 * a missing translation is never a reason to break a screen.
 *
 * Deliberately *not* a thrown error: during the module-by-module migration most namespaces are
 * still untranslated by design, so throwing would make development impossible rather than strict.
 */
export const missingKeys = new Set<string>();

const handleMissingKey = (locales: readonly string[], namespace: string, key: string): void => {
  const id = `${locales[0]}:${namespace}:${key}`;
  if (missingKeys.has(id)) return;
  missingKeys.add(id);
  if (import.meta.env.DEV) {
    console.error(`[i18n] missing key — ${id}`);
  }
};

/**
 * Load one namespace for one locale.
 *
 * A namespace that does not exist for a locale is not an error: during migration most namespaces
 * have an English catalogue and no Arabic one yet, and the correct behaviour is to fall back to
 * English rather than fail the route.
 */
export const loadNamespace = async (locale: LocaleCode, namespace: string): Promise<void> => {
  const id = `${locale}/${namespace}`;
  if (loaded.has(id)) return;

  // English is already in memory — registering it from the bundled copy avoids a second fetch and
  // guarantees the fallback language is present before anything renders.
  if (locale === DEFAULT_LOCALE) {
    const bundle = EN_CATALOGUE[namespace];
    if (bundle) i18next.addResourceBundle(locale, namespace, unflatten(bundle), true, true);
    loaded.add(id);
    return;
  }

  const importer = catalogues[cataloguePath(locale, namespace)];
  if (!importer) {
    loaded.add(id); // Remember the absence too, so we don't retry on every render.
    return;
  }

  const module = await importer();
  i18next.addResourceBundle(locale, namespace, module.default, true, true);
  loaded.add(id);
};

/**
 * Load a namespace and everything it falls back to.
 *
 * Without the fallback locales loaded, an Arabic user hitting a namespace with no Arabic catalogue
 * gets raw keys rather than English.
 */
export const ensureNamespace = async (locale: LocaleCode, namespace: string): Promise<void> => {
  const chain: LocaleCode[] = [locale, ...LOCALES[locale].fallback];
  await Promise.all(chain.map((l) => loadNamespace(l, namespace)));
};

export const ensureNamespaces = async (
  locale: LocaleCode,
  namespaces: readonly string[],
): Promise<void> => {
  await Promise.all(namespaces.map((ns) => ensureNamespace(locale, ns)));
};

let initialized = false;

export const initI18n = async (locale: LocaleCode = DEFAULT_LOCALE): Promise<I18nInstance> => {
  if (initialized) {
    await ensureNamespaces(locale, CORE_NAMESPACES);
    return i18next;
  }

  await i18next.use(initReactI18next).init({
    lng: locale,
    fallbackLng: LOCALES[locale].fallback.length ? [...LOCALES[locale].fallback] : DEFAULT_LOCALE,
    supportedLngs: LOCALE_CODES,
    defaultNS: DEFAULT_NAMESPACE,
    ns: [],
    // Catalogues are added by `loadNamespace`, never bundled up front.
    resources: {},
    partialBundledLanguages: true,
    interpolation: {
      // React escapes for us; double-escaping mangles apostrophes and quotes in both languages.
      escapeValue: false,
    },
    // Flat keys with dots would collide with antd field paths used as keys in some screens.
    keySeparator: '.',
    nsSeparator: ':',
    returnEmptyString: false,
    saveMissing: import.meta.env.DEV,
    missingKeyHandler: (lngs, ns, key) => handleMissingKey(lngs, ns, key),
    react: {
      // Suspense off: catalogues load per route and a suspended subtree here would unmount
      // screens mid-navigation. Components render English fallbacks until the bundle lands.
      useSuspense: false,
      // Re-render when a lazily-loaded catalogue is added to the store. Without this, a component
      // already mounted when its namespace arrives keeps its fallback text forever — react-i18next
      // does not listen for store events by default.
      bindI18nStore: 'added',
    },
  });

  initialized = true;
  await ensureNamespaces(locale, CORE_NAMESPACES);
  return i18next;
};

/** Switch language, making sure everything already on screen has a catalogue in the new one. */
export const changeI18nLocale = async (locale: LocaleCode): Promise<void> => {
  const active = Array.from(new Set([...CORE_NAMESPACES, ...(i18next.options.ns ?? [])])) as string[];
  await ensureNamespaces(locale, active);
  await i18next.changeLanguage(locale);
};

export default i18next;
